import React from "react";
import axios from "axios";
import { BASE_URL } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../redux/slices/userSlice";
import {
  customStringify,
  error_alert,
  getToken,
  removeToken,
} from "../../utils";

const useApi = () => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.language);

  let headers = {
    Accept: "application/json",
    "Accept-Language": `${lang ? lang: ""}`,
    // "Accept-Language": "it",
    // "Accept-Language": "en",
    // "Accept-Language": "zh-hans",
  };

  const Axios = axios.create({
    baseURL: BASE_URL + "/",
    headers: headers,
  });

  // Store a reference to the AbortControllers
  const abortControllers = React.useRef(new Map());

  // Helper function to extract the base URL without query params
  const getBaseUrl = (url) => {
    return url.split("?")[0];
  };

  Axios.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        config.headers["Authorization"] = "Bearer " + token;
      }

      // Extract the base URL without query parameters
      const baseUrl = getBaseUrl(config.url);

      // Cancel all ongoing requests with the same base URL
      const existingControllers = abortControllers.current.get(baseUrl);
      if (existingControllers) {
        existingControllers.forEach((controller) => controller.abort());
      }

      // Create a new AbortController for the request
      const controller = new AbortController();
      config.signal = controller.signal;

      // Store the controller in the map using the base URL as the key
      if (!abortControllers.current.has(baseUrl)) {
        abortControllers.current.set(baseUrl, []);
      }
      abortControllers.current.get(baseUrl).push(controller);

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  Axios.interceptors.response.use(
    (response) => {
      // Remove the controller from the map after the request completes
      const baseUrl = getBaseUrl(response.config.url);
      abortControllers.current.set(
        baseUrl,
        abortControllers.current
          .get(baseUrl)
          .filter((controller) => controller.signal !== response.config.signal)
      );
      if (abortControllers.current.get(baseUrl).length === 0) {
        abortControllers.current.delete(baseUrl);
      }
      return response;
    },
    (error) => {
      // Remove the controller from the map in case of an error
      if (error.config) {
        const baseUrl = getBaseUrl(error.config.url);
        abortControllers.current.set(
          baseUrl,
          abortControllers.current
            .get(baseUrl)
            .filter((controller) => controller.signal !== error.config.signal)
        );
        if (abortControllers.current.get(baseUrl).length === 0) {
          abortControllers.current.delete(baseUrl);
        }
      }

      if (error.response && error.response.status === 401) {
        dispatch(logout());
        removeToken();
        error_alert(customStringify(error.response.data.detail));
      } else if (error.response && error.response.status === 403) {
        dispatch(logout());
        removeToken();
        error_alert("Your session expired! Please log in again to continue");
      }

      return Promise.reject(error);
    }
  );

  // Function to cancel an API request by URL
  const cancelRequest = (url) => {
    const baseUrl = getBaseUrl(url);
    const controllers = abortControllers.current.get(baseUrl);
    if (controllers) {
      controllers.forEach((controller) => controller.abort());
      abortControllers.current.delete(baseUrl); // Clean up
    }
  };

  // Function to cancel all ongoing API requests
  const cancelAllRequests = () => {
    abortControllers.current.forEach((controllers) => {
      controllers.forEach((controller) => controller.abort());
    });
    abortControllers.current.clear(); // Clear all controllers from the map
  };

  return {
    Axios,
    cancelRequest,
    cancelAllRequests,
  };
};

export default useApi;
